import React, { useState, useEffect } from "react";
import { ChevronRight } from "lucide-react";
import ProductImagePlaceholder from "./ProductImagePlaceholder";
import "./BidCard.css";
import {
  LIVE_TIMER_INTERVAL_MS,
  MINUTES_IN_HOUR,
  MS_IN_MINUTE,
  MS_IN_SECOND,
  ONE_HOUR_IN_MS,
  SECONDS_IN_MINUTE,
  TWELVE_HOURS_IN_MS,
} from "./constants.bids";
import {
  BidCardContainer,
  BidCardTop,
  BidCardTitleRow,
  BidCardTitleBlock,
  BidCardTitle,
  BidCardId,
  BidMetaRow,
  BidMetaBox,
  BidMetaIcon,
  BidMetaLabel,
  ProductThumbsRow,
  ProductThumb,
  ProductThumbMore,
  BidCardFooter,
  BidStatusLabel,
  BidCardTimeWrapper,
  BidCardTimePrefix,
  BidCardTimeSuffix,
  CardShareBtn,
} from "./styles.bids";

const STATUS_CONFIG = {
  live:          { label: "Place bid",     arrow: true  },
  upcoming:      { label: "Upcoming",      arrow: false },
  awarded:       { label: "Awarded",       arrow: true  },
  completed:     { label: "Completed",     arrow: true  },
  submitted:     { label: "Submitted",     arrow: true  },
  not_awarded:   { label: "Not awarded",   arrow: true  },
  no_submission: { label: "No submission", arrow: false },
};

const LocationIcon = () => (
  <img src="/logos/bid_location.svg" width="22" height="22" alt="" />
);

const CalendarIcon = () => (
  <img src="/logos/bids_upcoming.svg" width="22" height="22" alt="" />
);

const RupeeIcon = () => (
  <img src="/logos/bid_amount.svg" width="22" height="22" alt="" />
);

const ShareIcon = () => <img src="/logos/bid_share.svg" width="20" height="20" alt="" />;

const formatTime = (date) => {
  const options = date.getMinutes() === 0
    ? { hour: "numeric" }
    : { hour: "numeric", minute: "2-digit" };

  return date.toLocaleTimeString("en-IN", options).replace(/\s?(am|pm)$/i, (match) => ` ${match.trim().toUpperCase()}`);
};

const formatEndDate = (endTime) => {
  const d = new Date(endTime);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" }) +
    ", " + formatTime(d);
};

const BidCard = ({ bid, onClick }) => {
  const {
    title,
    bidId,
    location,
    deliveryRange,
    totalAmount,
    products = [],
    productCount = 0,
    status,
    timeLabel,
    endTime,
  } = bid;

  const [copied, setCopied] = useState(false);

  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.live;
  const totalProductCount = productCount || products.length;
  const shouldShowMoreProducts = totalProductCount > 5;
  const visibleProducts = products.slice(0, shouldShowMoreProducts ? 4 : 5);
  const extraCount = shouldShowMoreProducts ? totalProductCount - visibleProducts.length : 0;
  const isLive = status === "live" || status === "submitted";

  const [liveDisplay, setLiveDisplay] = useState(null);

  useEffect(() => {
    if (!endTime || !isLive) { setLiveDisplay(null); return; }

    const update = () => {
      const diff = new Date(endTime) - Date.now();
      if (diff <= 0) {
        setLiveDisplay({ prefix: "Ended on ", suffix: formatEndDate(endTime) });
        return false;
      }

      if (diff <= ONE_HOUR_IN_MS) {
        // < 1 hour: show minutes and seconds
        const totalSecs = Math.floor(diff / MS_IN_SECOND);
        const mins = Math.floor(totalSecs / SECONDS_IN_MINUTE);
        const secs = totalSecs % SECONDS_IN_MINUTE;
        setLiveDisplay({ prefix: "Ends in ", suffix: `${mins}m ${String(secs).padStart(2, "0")}s` });
      } else if (diff <= TWELVE_HOURS_IN_MS) {
        // < 12 hours: show hours and minutes
        const totalMins = Math.floor(diff / MS_IN_MINUTE);
        const hrs = Math.floor(totalMins / MINUTES_IN_HOUR);
        const mins = totalMins % MINUTES_IN_HOUR;
        setLiveDisplay({ prefix: "Ends in ", suffix: `${hrs}h ${mins}m` });
      } else {
        // > 12 hours: show formatted date
        setLiveDisplay({ prefix: "Ends by ", suffix: formatEndDate(endTime) });
      }

      return true;
    };

    if (!update()) return;

    const id = setInterval(() => {
      if (!update()) clearInterval(id);
    }, LIVE_TIMER_INTERVAL_MS);
    return () => clearInterval(id);
  }, [endTime, isLive, status]);

  // Compute footer time display
  const timeDisplay = (() => {
    const label = (liveDisplay ? `${liveDisplay.prefix}${liveDisplay.suffix}` : timeLabel) || "";
    for (const p of ["Ends in ", "Ends by ", "Ended on ", "Starts on "]) {
      if (label.startsWith(p)) return { prefix: p, suffix: label.slice(p.length) };
    }
    return { prefix: "", suffix: label };
  })();

  return (
    <BidCardContainer onClick={onClick}>
      <BidCardTop>
        <BidCardTitleRow>
          <BidCardTitleBlock>
            <BidCardTitle>{title}</BidCardTitle>
            <BidCardId>#{bidId}</BidCardId>
          </BidCardTitleBlock>
          <CardShareBtn onClick={e => {
            e.stopPropagation();
            const url = `${window.location.origin}/bids/details/${bid.id}`;
            navigator.clipboard.writeText(url).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }).catch(() => {});
          }}>
            <ShareIcon />
          </CardShareBtn>
        </BidCardTitleRow>

        <BidMetaRow>
          <BidMetaBox>
            <BidMetaIcon><LocationIcon /></BidMetaIcon>
            <BidMetaLabel>{location}</BidMetaLabel>
          </BidMetaBox>
          <BidMetaBox>
            <BidMetaIcon><CalendarIcon /></BidMetaIcon>
            <BidMetaLabel>{deliveryRange}</BidMetaLabel>
          </BidMetaBox>
          <BidMetaBox>
            <BidMetaIcon><RupeeIcon /></BidMetaIcon>
            <BidMetaLabel>{totalAmount}</BidMetaLabel>
          </BidMetaBox>
        </BidMetaRow>

        <ProductThumbsRow>
          {visibleProducts.map((p, i) => (
            <ProductThumb key={i}>
              {p.imageUrl
                ? <img src={p.imageUrl} alt={p.name} className="bid-card-product-image" />
                : <ProductImagePlaceholder />}
            </ProductThumb>
          ))}
          {extraCount > 0 && (
            <ProductThumbMore>+{extraCount} more</ProductThumbMore>
          )}
        </ProductThumbsRow>
      </BidCardTop>

      <BidCardFooter $status={status}>
        <BidStatusLabel $status={status}>
          {cfg.label}
          {cfg.arrow && <ChevronRight size={17} strokeWidth={2.6} />}
        </BidStatusLabel>

        <BidCardTimeWrapper>
          <BidCardTimePrefix>{timeDisplay.prefix}</BidCardTimePrefix>
          <BidCardTimeSuffix $status={status}>{timeDisplay.suffix}</BidCardTimeSuffix>
        </BidCardTimeWrapper>
      </BidCardFooter>

      {copied && (
        <div className="bid-card-copy-toast">
          Link copied to clipboard
        </div>
      )}
    </BidCardContainer>
  );
};

export default BidCard;
